import { smBumps, useModal, xlBumps, xxlBumps } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'
import { useNavigate } from 'react-router'

import { getCurrentQuote } from '../../../../../domain'
import { useCheckoutBind } from '../../../../../hooks/checkout'
import { useEditProposalCallback } from '../../../../../hooks/proposal'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { useUpButton } from '../../../../../infrastructure/routing'
import { YourCoverSection } from '../../../../organisms/covers'
import { ProposalDocumentsSection } from '../../../../organisms/product'
import {
  ProposalAutoRenewalSection,
  ProposalInformationSection,
} from '../../../../organisms/proposal'
import { FullPage } from '../../../../templates'
import { useProduct } from '../../../ProductProvider'
import { ProposalSummaryPricing } from '../../pricing'
import { useProposal } from '../../ProposalProvider'
import { useCheckout } from '../CheckoutProvider'
import { OutstandingBalanceModal } from './components/OutstandingBalanceModal'
import { QuoteInformationSection } from './components/QuoteInformationSection'
import { StartPaymentSection } from './components/StartPaymentSection'
import { VerificationFailedModal } from './components/VerificationFailedModal'

const tKey = TKey('pages.proposal-summary')

export const ProposalSummaryPage: FunctionComponent = () => {
  const { t } = useWriskTranslation()

  const navigate = useNavigate()
  const modal = useModal()

  const { product } = useProduct()
  const { proposal } = useProposal()
  const { previous, next } = useCheckout()

  const upButton = useUpButton(previous)

  const quote = getCurrentQuote(proposal)

  const onEditDisclosures = useEditProposalCallback(proposal)

  const onCheckoutBind = useCheckoutBind(
    () => navigate(next.url),
    (checks) => {
      modal.show({
        content: <VerificationFailedModal checks={checks} />,
      })
    },
    () => modal.show({ content: <OutstandingBalanceModal /> }),
  )

  const autoRenewalSectionSubheader = t(tKey('sections.auto-renewal.subheader'), {
    defaultValue: null,
  })

  return (
    <FullPage
      pageId='proposal-summary-page'
      header={t(tKey('header'))}
      upButton={upButton}
    >
      <ProposalSummaryPricing
        product={product}
        proposal={proposal}
        quote={quote}
        mb={smBumps}
      />
      <ProposalInformationSection proposal={proposal} mb={xxlBumps} />
      <ProposalAutoRenewalSection
        proposal={proposal}
        mb={xxlBumps}
        subheader={autoRenewalSectionSubheader}
      />
      <QuoteInformationSection onEditDetails={onEditDisclosures} quote={quote} />
      <YourCoverSection quote={quote} />
      <ProposalDocumentsSection quote={quote} mb={xlBumps} />
      <StartPaymentSection
        product={product}
        onConfirm={onCheckoutBind.execute}
        loading={onCheckoutBind.loading}
      />
    </FullPage>
  )
}
